import { Component, OnDestroy, OnInit, ViewChild, ViewEncapsulation, AfterViewInit } from '@angular/core';
import { Title } from "@angular/platform-browser";
import { async, Observable, throwError, concat, of, from, Subscription } from 'rxjs';
import { map, switchMap, concatMap } from 'rxjs/operators';
import { PrimeNGConfig, LazyLoadEvent, MenuItem, MessageService, FilterMetadata } from 'primeng/api';
import { Table } from 'primeng/table';

import { environment } from 'src/environments/environment';

import { GetglobalsettingsService } from '../services/getglobalsettings.service'
import { Product, ProdPrice, ProductInfo, ProdVendors, ProductParts, ProductStat } from '../shared/product';
import { ProductGroup } from '../shared/product-group';
import { Currency } from '../shared/currency';
import { ProductApiService } from '../services/product-api.service';
import { DocumentApiService } from '../services/document-api.service';
import { ClientApiService } from '../services/client-api.service';
import { RestApiService } from '../services/rest-api.service';


@Component({
  selector: 'app-products',
  templateUrl: './products.component.html',
  styleUrls: ['./products.component.css']
})
/**
 * Product browser — tree-based group selector with a single-selection product list.
 *
 * `selectedGroupId` is persisted to `localStorage` under `'products-view-selected-group'`
 * so the user lands on the same group after navigating away.
 *
 * Three bookmarks (`productBookmark1/2/3`) let the user pin products and jump back to
 * them. Clicking an occupied bookmark navigates to its group; clicking it again clears it.
 *
 * This is the simpler sibling of `Products2Component` — no group management, no multi-select,
 * and no Excel export.
 */
export class ProductsComponent implements OnInit, OnDestroy {

  private productQuery: Subscription;
  private prodGroupQuery: Subscription;
  private prodInfoQuery: Subscription;
  private product2Query: Subscription;

  state: string = 'products-view';
  totalRecords: number;
  rows: number = 25;
  first: number;

  loading: boolean = false;

  selectedGroupId: number = 202; // 1241
  selectedProdId: number;

  selectedProduct: Product;

  productBookmark1: Product;
  productBookmark2: Product;
  productBookmark3: Product;

  /** `true` when no product is selected in the list. */
  public get ifThereNotSelection(): boolean { return this.selectedProduct ? false : true; }

  /** `true` when bookmark slot 1 is occupied. */
  public get isThereBookmark1(): boolean { return (this.productBookmark1) ? true : false; }
  /** `true` when bookmark slot 2 is occupied. */
  public get isThereBookmark2(): boolean { return (this.productBookmark2) ? true : false; }
  /** `true` when bookmark slot 3 is occupied. */
  public get isThereBookmark3(): boolean { return (this.productBookmark3) ? true : false; }

  /** `true` when the selected product belongs to a different group than the one currently displayed. */
  public get onParentGroup(): boolean {
    return (this.selectedProduct && (this.selectedProduct.PRODGROUP != this.selectedGroupId));
  }

  constructor(
    private restapi: RestApiService,
    private prodApi: ProductApiService,
    private docApi: DocumentApiService,
    private cliApi: ClientApiService,
    private primengconfig: PrimeNGConfig,
    private messageService: MessageService,
    private globalsettingsservice: GetglobalsettingsService,
    private titleService: Title
  ) { }

  /** Applies the group selected in the tree widget and stores it to `selectedGroupId`. */
  selectedGroup(event) {
    console.log('*** ProductsComponent selectedGroup: ' + JSON.stringify(event));

    this.selectedGroupId = event.PGROUPID;
  }

  /** Persists `selectedGroupId` to localStorage and unsubscribes all queries. */
  ngOnDestroy(): void {
    console.log('*** ProductsComponent ngOnDestroy: ');

    localStorage.setItem(this.state + '-selected-group', JSON.stringify(this.selectedGroupId));

    if (this.productQuery) { this.productQuery.unsubscribe(); }

    if (this.prodGroupQuery) { this.prodGroupQuery.unsubscribe(); }

    if (this.prodInfoQuery) { this.prodInfoQuery.unsubscribe(); }

    if (this.product2Query) { this.product2Query.unsubscribe(); }
  }

  /** Restores last `selectedGroupId` from localStorage. */
  ngOnInit(): void {
    console.log('*** ProductsComponent ngOnInit: ');

    this.titleService.setTitle('Товары');

    let storedGroup: string = localStorage.getItem(this.state + '-selected-group');

    if (storedGroup) {
      this.selectedGroupId = JSON.parse(storedGroup);
    }
  }

  /** Stores the product selected from the child product-list component. */
  productSelection(event) {
    console.log('*** ProductsComponent productSelection: ' + JSON.stringify(event));
    this.selectedProduct = event;
  }

  /** Navigates the tree to the selected product's own group. */
  goToProductGroup(event) {
    console.log('*** ProductsComponent goToProductGroup: ' + JSON.stringify(event));
    environment.production || console.table(this.selectedProduct);

    this.selectedGroupId = this.selectedProduct.PRODGROUP;

  }

  /** Pins the selected product to bookmark 1, or navigates to the pinned product and clears the slot. */
  markBookmark1($event) {
    if (this.productBookmark1) {
      console.log('*** ProductsComponent markBookmark1: (moving) ' + this.productBookmark1.PRODID);

      this.selectedGroupId = this.productBookmark1.PRODGROUP;
      this.selectedProdId = this.productBookmark1.PRODID;

      //this.scrollToDocument(this.productBookmark1.PRODID)
      this.productBookmark1 = null;
    } else if (this.selectedProduct) {
      this.productBookmark1 = this.selectedProduct;
      console.log('*** ProductsComponent markBookmark1: (marking) ' + this.productBookmark1.PRODID);
    }
  }

  /** Pins the selected product to bookmark 2, or navigates to the pinned product and clears the slot. */
  markBookmark2($event) {
    if (this.productBookmark2) {
      console.log('*** ProductsComponent markBookmark2: (moving) ' + this.productBookmark2.PRODID);

      this.selectedGroupId = this.productBookmark2.PRODGROUP;
      this.selectedProdId = this.productBookmark2.PRODID;

      //this.scrollToDocument(this.productBookmark2.PRODID)
      this.productBookmark2 = null;
    } else if (this.selectedProduct) {
      this.productBookmark2 = this.selectedProduct;
      console.log('*** ProductsComponent markBookmark2: (marking) ' + this.productBookmark2.PRODID);
    }
  }

  /** Pins the selected product to bookmark 3, or navigates to the pinned product and clears the slot. */
  markBookmark3($event) {
    if (this.productBookmark3) {
      console.log('*** ProductsComponent markBookmark3: (moving) ' + this.productBookmark3.PRODID);

      this.selectedGroupId = this.productBookmark3.PRODGROUP;
      this.selectedProdId = this.productBookmark3.PRODID;

      //this.scrollToDocument(this.productBookmark3.PRODID)
      this.productBookmark3 = null;
    } else if (this.selectedProduct) {
      this.productBookmark3 = this.selectedProduct;
      console.log('*** ProductsComponent markBookmark3: (marking) ' + this.productBookmark3.PRODID);
    }
  }
}
